import { z } from 'zod';
import { brandedUuid } from './ids.ts';

/**
 * Proactive-messaging ("she texts you first") contracts — the typed shapes that
 * cross the proactive seam. The scheduling MATH lives behind the backend
 * `services/proactive/*` (schedule / quiet-hours / gate); this file owns only the
 * wire SHAPE so the settings API, the backend gate, and the `apps/jobs` firing
 * worker agree on what a stored `proactive_schedules.quiet_hours` looks like.
 *
 * See docs/architecture/proactive-messaging.md § "Quiet hours".
 */

/**
 * A wall-clock time of day, `HH:MM` on a 24-hour clock (`00:00`–`23:59`). Always
 * interpreted in the USER's IANA timezone (`users.timezone`), never server-local
 * or UTC — so a stored window follows the user across DST automatically.
 */
export const TimeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected a HH:MM time of day (00:00–23:59)');
export type TimeOfDay = z.infer<typeof TimeOfDay>;

/**
 * The quiet-hours window stored in `proactive_schedules.quiet_hours` (jsonb),
 * expressed in the user's timezone. `start`/`end` are inclusive-start,
 * exclusive-end; `start > end` wraps past midnight (e.g. `22:00`–`07:00`). A
 * `null` column means "no quiet hours". `start === end` is rejected — it is
 * ambiguous (empty vs all-day); persist `null` to disable instead.
 *
 * A proactive fire is NEVER delivered inside this window: the gate skips it and
 * the scheduler clamps `next_fire_at` forward to the window's `end`.
 */
export const QuietHours = z
  .object({
    /** Window start, user-tz `HH:MM` (inclusive). */
    start: TimeOfDay,
    /** Window end, user-tz `HH:MM` (exclusive); `< start` wraps past midnight. */
    end: TimeOfDay,
  })
  .refine((w) => w.start !== w.end, {
    message: 'quiet-hours start and end must differ (use null for no quiet hours)',
  });
export type QuietHours = z.infer<typeof QuietHours>;

/**
 * The BullMQ payload for one proactive `fire` — enqueued by the repeatable sweep
 * (or the `scripts/proactive/fire.ts` fast-forward) for a schedule that is due.
 * `scheduleId` + `nextFireAt` (the beat this fire represents) form the stable
 * idempotency key: a double-enqueue from overlapping sweeps or a BullMQ retry
 * collapses to ONE fire (mirrors `MemoryExtractJobData` / `MediaGenerateJobData`).
 * The worker loads the authoritative schedule row from `(accountId, scheduleId)`,
 * so the payload stays minimal — no schedule state is trusted off the wire.
 *
 * See docs/architecture/proactive-messaging.md § "De-dupe".
 */
export const ProactiveFireJobData = z.object({
  accountId: brandedUuid('AccountId'),
  scheduleId: brandedUuid('ProactiveScheduleId'),
  /** The beat this fire represents (ISO-8601), pairing with `scheduleId` as the dedupe key. */
  nextFireAt: z.string().datetime(),
});
export type ProactiveFireJobData = z.infer<typeof ProactiveFireJobData>;
